import React, { useState, useMemo } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Drawer,
  Empty,
  Flex,
  FloatButton,
  Grid,
  Row,
} from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { Map, Set } from "immutable";
import { useTranslation } from "react-i18next";
import { useAntdTheme } from "../../shared/hooks";
import { useErrorNotification } from "./hooks/useErrorNotification";
import { useOrderPriorityButton } from "./hooks/useOrderPriorityButton";
import { useResponsiveFontSize } from "./hooks/useResponsiveFontSize";

import ButtonWithLoading from "../../shared/components/buttons/ButtonWithLoading";
import PrintButton from "../../shared/components/buttons/PrintButton";
import ReprintItemLabelButton from "../../shared/components/buttons/ReprintItemLabelButton";
import PrintLabelItemButton from "../../shared/components/buttons/PrintLabelItemButton";
import PrintNonLabelItemButton from "../../shared/components/buttons/PrintNonLabelItemButton";
import { BaseOrderColumn } from "./BaseOrderColumn";
import { printOnOrderDone } from "../../shared/utils/Print";

import { postFetchEmptyBody, sortItems, API_URL, STATUS_DISPLAY } from "../../shared/utils/Util";

const Component: React.FC<any> = ({
  orders,
  renderOrder,
  renderOrderTitle,
  restaurantId,
  showItemSelect,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const screens = Grid.useBreakpoint();
  const { showError } = useErrorNotification();
  const { renderStarButton } = useOrderPriorityButton({ restaurantId, orders });
  const { getButtonFontSize, getButtonMinWidth } = useResponsiveFontSize();
  // Selected item attribute ID
  const [selectedItem, setSelectedItem] = useState(null);
  // Drawer state for mobile filter
  const [drawerOpen, setDrawerOpen] = useState(false);

  const getFilterTitleFontSize = () => {
    if (screens.xl) return "18px";
    if (screens.lg) return "17px";
    if (screens.md) return "16px";
    return "15px";
  };

  const getFilterTextFontSize = () => {
    if (screens.xl) return "16px";
    if (screens.lg) return "15px";
    if (screens.md) return "14px";
    return "13px";
  };

  const getFilterButtonFontSize = () => {
    if (screens.xl) return "18px";
    if (screens.lg) return "17px";
    if (screens.md) return "16px";
    return "15px";
  };

  const { itemMap, items } = useMemo(() => {
    let map = Map<string, { item: any; orders: Set<string> }>();
    orders.forEach((order) => {
      if (order.items) {
        order.items.forEach((item) => {
          const collapsedItemID = item.attributes_id;
          if (!map.has(collapsedItemID)) {
            map = map.set(collapsedItemID, {
              item: item,
              orders: Set(),
            });
          }
          const matchingCollapsedItem = map.get(collapsedItemID);
          if (matchingCollapsedItem) {
            map = map.set(collapsedItemID, {
              ...matchingCollapsedItem,
              orders: matchingCollapsedItem.orders.add(order.id),
            });
          }
        });
      }
    });
    let itemsArray = map
      .valueSeq()
      .toArray()
      .map((itemValue) => itemValue.item);
    const sortedItems = sortItems(itemsArray);

    return { itemMap: map, items: sortedItems };
  }, [orders]);

  const renderStartedOrder = (order) => (
    <div style={{ width: "100%" }}>{renderOrder(order, order.items ? order.items.length : 0)}</div>
  );

  const renderStartedOrderDetails = (order) => {
    const items = sortItems(order.items);

    // Responsive button sizing - includes landscape phones
    const isMobile = !screens.lg;
    const buttonSize = isMobile ? "middle" : "large";
    const buttonHeight = isMobile ? token.controlHeight : token.controlHeightLG;
    const buttonFontSize = isMobile ? "14px" : "15px";
    const buttonMinWidth = isMobile ? "120px" : "150px";

    return (
      <div style={{ fontSize: "15px", lineHeight: 1.6 }}>
        {(!items || items.length === 0 || !order.pos_order_name) && (
          <Alert
            message={t("order_incomplete_warning")}
            type="warning"
            showIcon
            style={{
              marginBottom: token.marginLG,
              borderRadius: token.borderRadius,
            }}
          />
        )}

        {order.notes && (
          <div
            style={{
              padding: token.marginMD,
              background: token.colorBgLayout,
              borderRadius: token.borderRadius,
              marginBottom: token.marginLG,
              borderLeft: `4px solid ${token.colorPrimary}`,
            }}
          >
            <strong>{t("notes")}：</strong>
            {order.notes}
          </div>
        )}

        <div
          style={{
            marginBottom: token.marginLG,
            display: "flex",
            gap: isMobile ? token.marginSM : token.marginMD,
            flexWrap: "wrap",
          }}
        >
          <PrintButton
            key="print"
            size={buttonSize}
            style={{
              height: buttonHeight,
              fontSize: buttonFontSize,
              fontWeight: token.fontWeightStrong,
              borderRadius: token.borderRadius,
              flex: 1,
              minWidth: buttonMinWidth,
            }}
            handleError={() => {
              showError("unable_to_print");
            }}
            order={order}
          >
            {t("print_order_ticket")}
          </PrintButton>
          <PrintLabelItemButton
            key="print-drink-labels"
            size={buttonSize}
            style={{
              height: buttonHeight,
              fontSize: buttonFontSize,
              fontWeight: token.fontWeightStrong,
              borderRadius: token.borderRadius,
              flex: 1,
              minWidth: buttonMinWidth,
            }}
            handleError={() => {
              showError("unable_to_print");
            }}
            order={order}
          >
            {t("print_beverage_labels")}
          </PrintLabelItemButton>
          <PrintNonLabelItemButton
            key="print-all-items"
            size={buttonSize}
            style={{
              height: buttonHeight,
              fontSize: buttonFontSize,
              fontWeight: token.fontWeightStrong,
              borderRadius: token.borderRadius,
              flex: 1,
              minWidth: buttonMinWidth,
            }}
            handleError={() => {
              showError("unable_to_print");
            }}
            order={order}
          >
            {t("print_non_beverage_labels")}
          </PrintNonLabelItemButton>
        </div>

        {items && items.length > 0 && (
          <div
            style={{
              border: `1px solid ${token.colorBorder}`,
              borderRadius: token.borderRadius,
              overflow: "hidden",
            }}
          >
            {items.map((item, index) => {
              const itemActionStatus = order.itemActionStatuses.get(item.id);
              const isLastItem = index === items.length - 1;

              return (
                <div
                  key={item.id}
                  style={{
                    padding: token.marginMD,
                    borderBottom: isLastItem ? "none" : `1px solid ${token.colorBorder}`,
                    background: index % 2 === 0 ? token.colorBgContainer : token.colorBgLayout,
                  }}
                >
                  <Row gutter={[16, 8]} align="middle">
                    <Col span={18}>
                      <Checkbox
                        checked={!!item.done_time}
                        onChange={(e) => {
                          let url = "";
                          if (e.target.checked) {
                            url = `${API_URL}/v1/r/${restaurantId}/items/${item.id}/done`;
                          } else {
                            url = `${API_URL}/v1/r/${restaurantId}/items/${item.id}/undo_done`;
                          }
                          postFetchEmptyBody(url, () => {
                            showError();
                          });
                        }}
                        style={{
                          fontSize: "16px",
                        }}
                      >
                        <div style={{ marginLeft: token.marginSM }}>
                          <div
                            style={{
                              fontWeight: token.fontWeightStrong,
                              fontSize: "15px",
                              marginBottom: token.marginXS,
                            }}
                          >
                            {item.attributes.alternate_name
                              ? item.attributes.alternate_name
                              : item.attributes.name}
                            {itemActionStatus != null && (
                              <span
                                style={{
                                  marginLeft: token.marginSM,
                                  color: token.colorTextSecondary,
                                  fontWeight: token.fontWeightStrong,
                                }}
                              >
                                - {STATUS_DISPLAY[itemActionStatus]}
                              </span>
                            )}
                          </div>
                          {(item.attributes.notes || item.attributes.modifications) && (
                            <div
                              style={{
                                fontSize: "14px",
                                color: token.colorTextSecondary,
                                marginTop: token.marginXS,
                              }}
                            >
                              {item.attributes.notes && <div>{item.attributes.notes}</div>}
                              {item.attributes.modifications && (
                                <div>{item.attributes.modifications.join(", ")}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </Checkbox>
                    </Col>
                    <Col span={6}>
                      {item.print_label && (
                        <ReprintItemLabelButton
                          key={`print-item-label-${item.id}`}
                          size="small"
                          style={{
                            width: "100%",
                            fontSize: "13px",
                            fontWeight: token.fontWeightStrong,
                            borderRadius: token.borderRadiusSM,
                          }}
                          handleError={() => {
                            showError("unable_to_reprint_labels");
                          }}
                          order={order}
                          item={item}
                        >
                          {t("reprint_labels")}
                        </ReprintItemLabelButton>
                      )}
                    </Col>
                  </Row>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderDoneButton = (order, className, style) => {
    // Check if this is for modal (has specific height set) or for row (needs full height)
    const isModal = style && style.height && style.height !== "100%";

    if (isModal) {
      // Modal button - use provided styles
      return (
        <ButtonWithLoading
          key="done"
          size="large"
          style={{
            ...style,
            color: "#fff",
            transition: "all 0.2s",
          }}
          postURL={`${API_URL}/v1/r/${restaurantId}/orders/${order.id}/done`}
          postHandleResponse={() => {
            printOnOrderDone(order, () => {
              showError("unable_to_print_points");
            });
          }}
          postHandleError={() => {
            showError();
          }}
        >
          {t("done_button")}
        </ButtonWithLoading>
      );
    }

    // Row button - full height layout
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <ButtonWithLoading
          key="done"
          style={{
            flex: 1,
            minWidth: getButtonMinWidth(),
            fontSize: getButtonFontSize(),
            fontWeight: 600,
            borderRadius: 0,
            height: "100%",
            background: token.colorSuccess,
            borderColor: "#52c41a",
            color: "#fff",
            transition: "all 0.2s",
            ...style,
          }}
          postURL={`${API_URL}/v1/r/${restaurantId}/orders/${order.id}/done`}
          postHandleResponse={() => {
            printOnOrderDone(order, () => {
              showError("unable_to_print_points");
            });
          }}
          postHandleError={() => {
            showError();
          }}
        >
          {t("done_button")}
        </ButtonWithLoading>
      </div>
    );
  };

  const heading = t("in_progress");

  const renderModalFooter = (elem: any, defaultFooter: React.ReactNode) => {
    const isMobile = !screens.lg;
    const footerButtonSize = isMobile ? "middle" : "large";
    const footerButtonHeight = isMobile ? token.controlHeight : token.controlHeightLG;
    const footerButtonFontSize = isMobile ? "14px" : "16px";
    const footerButtonMinWidth = isMobile ? "100px" : "120px";

    if (isMobile) {
      // Mobile: Stack buttons vertically, full-width
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: token.marginSM,
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: token.marginSM,
              width: "100%",
            }}
          >
            {React.cloneElement(defaultFooter as React.ReactElement<any>, {
              block: true,
              style: {
                ...((defaultFooter as React.ReactElement<any>).props.style || {}),
                height: footerButtonHeight,
                fontSize: footerButtonFontSize,
                flex: 1,
              },
            })}
            {renderDoneButton(elem, "", {
              height: footerButtonHeight,
              fontSize: footerButtonFontSize,
              fontWeight: token.fontWeightStrong,
              borderRadius: token.borderRadius,
              background: token.colorSuccess,
              borderColor: token.colorSuccess,
              flex: 1,
            })}
          </div>
          <ButtonWithLoading
            key="undo"
            size={footerButtonSize}
            block
            style={{
              height: footerButtonHeight,
              fontSize: footerButtonFontSize,
              fontWeight: token.fontWeightStrong,
              borderRadius: token.borderRadius,
            }}
            postURL={`${API_URL}/v1/r/${restaurantId}/orders/${elem.id}/undo`}
            postHandleError={() => {
              showError();
            }}
          >
            {t("not_started")}
          </ButtonWithLoading>
        </div>
      );
    }

    // Desktop: Original horizontal layout
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: token.marginMD,
        }}
      >
        <ButtonWithLoading
          key="undo"
          size={footerButtonSize}
          style={{
            height: footerButtonHeight,
            fontSize: footerButtonFontSize,
            fontWeight: token.fontWeightStrong,
            borderRadius: token.borderRadius,
            minWidth: footerButtonMinWidth,
          }}
          postURL={`${API_URL}/v1/r/${restaurantId}/orders/${elem.id}/undo`}
          postHandleError={() => {
            showError();
          }}
        >
          {t("not_started")}
        </ButtonWithLoading>
        <div
          style={{
            display: "flex",
            gap: token.marginMD,
            alignItems: "center",
          }}
        >
          {defaultFooter}
          {renderDoneButton(elem, "", {
            height: footerButtonHeight,
            fontSize: footerButtonFontSize,
            fontWeight: token.fontWeightStrong,
            borderRadius: token.borderRadius,
            minWidth: footerButtonMinWidth,
            background: token.colorSuccess,
            borderColor: token.colorSuccess,
          })}
        </div>
      </div>
    );
  };

  const renderOrderCol = (elems, showHeading, elemsMaxHeight = "none") => (
    <BaseOrderColumn
      orders={elems}
      renderOrder={renderStartedOrder}
      renderOrderTitle={renderOrderTitle}
      renderOrderDetails={renderStartedOrderDetails}
      renderLeftButton={renderStarButton}
      renderRightButton={(order) => renderDoneButton(order, "", {})}
      renderModalFooter={renderModalFooter}
      emptyDescription={t("no_orders")}
      heading={heading}
      showHeading={showHeading}
      elemsMaxHeight={elemsMaxHeight}
      modalCentered={true}
      modalZIndex={1050}
      modalDestroyOnHidden={true}
      useCustomModalStyles={true}
    />
  );

  const renderItem = (item) => {
    const attributes = item.attributes;
    const isSelected = selectedItem === item.attributes_id;
    return (
      <Card
        key={item.id}
        hoverable
        styles={{
          body: {
            fontSize: 17,
            padding: token.marginLG,
            color: token.colorText,
          },
        }}
        onClick={() => {
          setSelectedItem(item.attributes_id);
          // Close drawer when item is selected on mobile/tablet
          if (!screens.lg) {
            setDrawerOpen(false);
          }
        }}
        style={{
          marginBottom: token.marginMD,
          borderRadius: token.borderRadiusLG,
          border: isSelected ? `2px solid ${token.colorPrimary}` : `1px solid ${token.colorBorder}`,
          boxShadow: isSelected ? `0 4px 12px ${token.colorPrimary}30` : token.boxShadowSecondary,
          background: isSelected ? `${token.colorPrimaryBg}` : token.colorBgContainer,
          cursor: "pointer",
        }}
      >
        <div
          style={{
            fontWeight: token.fontWeightStrong,
            marginBottom: token.marginSM,
            fontSize: "17px",
            color: isSelected ? token.colorPrimary : token.colorText,
          }}
        >
          {attributes.alternate_name ? attributes.alternate_name : attributes.name}
        </div>
        {(attributes.notes || attributes.modifications) && (
          <div style={{ fontSize: "14px", color: token.colorTextSecondary }}>
            {attributes.notes && <div style={{ marginBottom: "4px" }}>{attributes.notes}</div>}
            {attributes.modifications && <div>{attributes.modifications.join(", ")}</div>}
          </div>
        )}
      </Card>
    );
  };

  if (showItemSelect) {
    let filteredOrders = orders;
    let selectedItemInfo: string[] = [];
    if (selectedItem && itemMap.has(selectedItem)) {
      // itemMap should always contain this item attributes ID
      const selectedItemObj = itemMap.get(selectedItem);

      if (selectedItemObj) {
        const selectedOrders = selectedItemObj.orders;
        filteredOrders = orders.filter((order) => {
          return selectedOrders.has(order.id);
        });

        const selectedItemAttributes = selectedItemObj.item.attributes;
        if (selectedItemAttributes.alternate_name) {
          selectedItemInfo.push(selectedItemAttributes.alternate_name);
        } else {
          selectedItemInfo.push(selectedItemAttributes.name);
        }
        if (selectedItemAttributes.notes) {
          selectedItemInfo.push(selectedItemAttributes.notes);
        }
        if (selectedItemAttributes.modifications) {
          selectedItemInfo.push(selectedItemAttributes.modifications.join(", "));
        }
      }
    }

    const renderFilterPanel = () => {
      if (items.length === 0) {
        return (
          <Card
            style={{
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorBorder}`,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: token.colorBgContainer,
            }}
          >
            <Empty description={t("no_items")} />
          </Card>
        );
      }

      return (
        <div
          style={{
            maxHeight: screens.lg ? "calc(100vh - 200px)" : "none",
            overflowY: screens.lg ? "auto" : "visible",
          }}
        >
          {selectedItem && itemMap.has(selectedItem) ? (
            <div
              style={{
                background: token.colorBgLayout,
                padding: token.marginLG,
                borderRadius: token.borderRadiusLG,
                border: `1px solid ${token.colorBorder}`,
              }}
            >
              <div
                style={{
                  fontSize: getFilterTitleFontSize(),
                  fontWeight: token.fontWeightStrong,
                  marginBottom: token.marginMD,
                  color: token.colorText,
                }}
              >
                {t("filter_by_item")}
              </div>
              <div
                style={{
                  margin: `${token.marginMD} 0`,
                  fontWeight: token.fontWeightStrong,
                  fontSize: getFilterTextFontSize(),
                  color: token.colorText,
                }}
              >
                {selectedItemInfo.map((info, idx) => (
                  <div key={idx} style={{ marginBottom: token.marginXS }}>
                    {info}
                  </div>
                ))}
              </div>
              <Button
                size="large"
                onClick={() => {
                  setSelectedItem(null);
                  // Close drawer when clearing filter on mobile/tablet
                  if (!screens.lg) {
                    setDrawerOpen(false);
                  }
                }}
                block
                style={{
                  height: token.controlHeightLG,
                  fontSize: getFilterButtonFontSize(),
                  fontWeight: 600,
                  borderRadius: token.borderRadius,
                }}
              >
                {t("cancel")}
              </Button>
            </div>
          ) : (
            <div>{items.map(renderItem)}</div>
          )}
        </div>
      );
    };

    return (
      <>
        <Card
          className="control-station-tabs"
          style={{
            flex: 1,
            borderRadius: token.borderRadiusLG,
            boxShadow: token.boxShadowSecondary,
            overflow: "hidden",
            background: token.colorPrimary,
            display: "flex",
            flexDirection: "column",
          }}
          title={`${heading} (${orders.length})`}
          styles={{
            body: { height: "100%", display: "flex", flexDirection: "column", padding: "20px" },
          }}
        >
          <Row gutter={{ xs: 8, sm: 12, md: 16 }} style={{ height: "100%" }}>
            {/* Filter column - only shown on large screens and above */}
            {screens.lg && (
              <Col xs={0} sm={0} md={0} lg={7}>
                {renderFilterPanel()}
              </Col>
            )}
            <Col xs={24} sm={24} md={24} lg={17}>
              {renderOrderCol(filteredOrders, false, "calc(100vh - 200px)")}
            </Col>
          </Row>
        </Card>

        {/* Mobile drawer for filters */}
        {!screens.lg && items.length > 0 && (
          <>
            <Drawer
              title={t("filter_by_item")}
              placement="left"
              onClose={() => setDrawerOpen(false)}
              open={drawerOpen}
              width={320}
              styles={{
                body: {
                  padding: token.paddingLG,
                },
              }}
            >
              {renderFilterPanel()}
            </Drawer>

            <FloatButton
              icon={<FilterOutlined />}
              type="primary"
              style={{
                right: 24,
                bottom: 24,
              }}
              onClick={() => setDrawerOpen(true)}
              badge={{
                dot: selectedItem !== null,
                color: token.colorSuccess,
              }}
              tooltip={<div>{t("filter_by_item")}</div>}
            />
          </>
        )}
      </>
    );
  } else {
    return renderOrderCol(orders, true, "calc(100vh - 200px)");
  }
};

export default Component;
